import { useQuery } from '@tanstack/react-query'
import { api, cents, day, month, points } from '../../lib'
import { Empty, ErrorBox, Loading } from '../../app/ui'
import { Err, PartnerPage, useAction } from './common'
import type { Bill } from './types'

const STATUS: Record<Bill['status'], string> = {
  draft: 'Preparing',
  sent: 'Due',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
  zero: 'Nothing due',
}

export function BillsPage() {
  return <PartnerPage>{(pid) => <BillsBody pid={pid} />}</PartnerPage>
}

function BillsBody({ pid }: { pid: string }) {
  const q = useQuery({ queryKey: ['partners', 'bills', pid], queryFn: () => api<{ rows: Bill[] }>('GET', `/api/partners/${pid}/bills`) })
  const pay = useAction((runId: string) => api('POST', `/dev/billing/${runId}/pay`, {}))
  if (q.error) return <ErrorBox error={q.error} />
  if (!q.data) return <Loading />
  return (
    <div className="stack">
      <h1>Bills</h1>
      {q.data.rows.length === 0 ? (
        <Empty>No bills yet.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="stack-table" data-testid="partners-bills">
            <thead>
              <tr>
                <th>Month</th>
                <th className="right">Points</th>
                <th className="right">Refunds</th>
                <th className="right">Amount</th>
                <th>Status</th>
                <th>Due</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((b) => (
                <tr key={b.run_id} data-testid={`partners-bill-${b.period.slice(0, 7)}`}>
                  <td data-label="Month">{month(b.period)}</td>
                  <td className="right" data-label="Points">{points(b.points_billed)}</td>
                  <td className="right" data-label="Refunds">{b.credits_cents ? cents(-b.credits_cents) : b.carried_credit_cents ? `${cents(b.carried_credit_cents)} next bill` : '—'}</td>
                  <td className="right" data-label="Amount" data-testid="partners-bill-amount">
                    {cents(b.amount_cents)}
                  </td>
                  <td data-label="Status" data-testid="partners-bill-status">{STATUS[b.status]}{b.returned ? ' (payment returned)' : ''}</td>
                  <td data-label="Due">{day(b.due_at)}</td>
                  <td className="row">
                    <a href={`/api/bills/${b.run_id}/statement`} target="_blank" rel="noreferrer">
                      Details
                    </a>
                    <a href={`/api/bills/${b.run_id}/statement.csv`}>Download</a>
                    {(b.status === 'sent' || b.status === 'overdue') && b.hosted_url?.startsWith('/dev/') && (
                      <button className="primary" data-testid="partners-bill-pay" disabled={pay.busy} onClick={() => pay.run(b.run_id)}>
                        Pay this bill (test)
                      </button>
                    )}
                    {(b.status === 'sent' || b.status === 'overdue') && !b.returned && b.hosted_url && !b.hosted_url.startsWith('/dev/') && (
                      <a className="button primary" href={b.hosted_url}>
                        Pay
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Err error={pay.error} />
    </div>
  )
}
